/**
 * Reusable Scoring Modal Module
 */
import { showErrorModal } from './krida-ui-error.js';

// Module-level variables
let scoringModalElement = null;
let scoringModalBody = null;
let scoringContentLoaded = false;
let isScoringInitialized = false;
let initializationAttempted = false;

const SCORING_MODAL_PATH = '/scoring-modal.html';
const SCORING_CONTENT_PATH = '/scoring-content.html';

/**
 * Fetches the modal HTML, injects it, fetches scoring content, and sets up listeners.
 * Should be called once on page load.
 * @param {boolean} showErrorOnFail - If true, shows error modal on failure. False for background init.
 * @returns {Promise<boolean>} - True if initialization succeeded, false otherwise.
 */
export async function initScoringModal(showErrorOnFail = false) {
    if (isScoringInitialized) {
        console.log("Scoring Modal already initialized.");
        return true;
    }

    initializationAttempted = true;
    console.log("Initializing Shared Scoring Modal...");

    try {
        // 1. Fetch and Inject Modal HTML (if not already present)
        if (!document.getElementById('scoring-modal')) {
            const response = await fetch(SCORING_MODAL_PATH);
            if (!response.ok) throw new Error(`Failed to fetch scoring modal structure: ${response.status}`);
            const modalHtml = await response.text();
            document.body.insertAdjacentHTML('beforeend', modalHtml);
            console.log("Scoring modal HTML injected.");
        }

        // 2. Get Element References
        scoringModalElement = document.getElementById('scoring-modal');
        if (!scoringModalElement) throw new Error("Scoring modal element not found after injection.");

        scoringModalBody = scoringModalElement.querySelector('#scoring-modal-body');
        const closeButton = scoringModalElement.querySelector('.close-button');
        const footerCloseButton = scoringModalElement.querySelector('#scoring-modal-close-btn');

        if (!scoringModalBody) throw new Error("Scoring modal body not found.");
        if (!closeButton) throw new Error("Scoring modal header close button not found.");
        if (!footerCloseButton) throw new Error("Scoring modal footer close button not found.");

        // 3. Add Event Listeners
        closeButton.addEventListener('click', closeScoringModal);
        footerCloseButton.addEventListener('click', closeScoringModal);
        // Optional: Close on overlay click (if your .modal class has an overlay behavior)
        scoringModalElement.addEventListener('click', (event) => {
            if (event.target === scoringModalElement) { // Clicked on modal background
                closeScoringModal();
            }
        });

        // 4. Load Scoring Content (if not already loaded)
        if (!scoringContentLoaded) {
            const contentResponse = await fetch(SCORING_CONTENT_PATH);
            if (!contentResponse.ok) throw new Error(`Failed to fetch scoring content: ${contentResponse.status}`);
            const contentHtml = await contentResponse.text();
            scoringModalBody.innerHTML = contentHtml;
            scoringContentLoaded = true;
            console.log("Scoring content loaded into modal.");
        }

        isScoringInitialized = true;
        console.log("Shared Scoring Modal initialized successfully.");
        return true;

    } catch (error) {
        console.error("Error initializing scoring modal:", error);
        // Only show error modal if explicitly requested (user-initiated action)
        if (showErrorOnFail && showErrorModal) {
            showErrorModal("Scoring Error", "Could not load scoring information. Please try again.");
        }
        return false;
    }
}

/**
 * Opens the scoring modal.
 */
export async function openScoringModal() {
    if (!isScoringInitialized) {
        console.warn("Scoring modal not initialized. Attempting to initialize now.");
        // User explicitly requested to view scoring, so show errors if init fails
        const success = await initScoringModal(true);
        if (!success) {
            // Error modal already shown by initScoringModal
            return;
        }
    }
    // Now, check if initialization was successful (or was already true)
    if (scoringModalElement && isScoringInitialized) {
        scoringModalElement.classList.remove('hidden');
        scoringModalElement.classList.add('active');
        console.log("Scoring modal opened.");
    } else {
        console.error("Cannot open scoring modal: Element not found or initialization failed.");
        if (showErrorModal) {
            showErrorModal("Scoring Error", "Could not display scoring information.");
        }
    }
}

/**
 * Closes the scoring modal.
 */
export function closeScoringModal() {
    if (scoringModalElement) {
        scoringModalElement.classList.add('hidden');
        scoringModalElement.classList.remove('active'); // Remove active class
        console.log("Scoring modal closed.");
    }
}

// Initialize on script load for convenience if this script is added late,
// but primary initialization should be driven by main page scripts.
// initScoringModal(); // Consider if auto-init is desired or if main scripts should call it. 